// ============================================================================
// notifications.js — push értesítések és az alkalmazásikon jelvénye.
//
// HÁROM DOLGOT KEZEL:
//
//  1. ENGEDÉLYKÉRÉS: a böngésző csak felhasználói gesztusra engedi. Nem
//     kérünk engedélyt rögtön a belépéskor - az tolakodó, és a felhasználók
//     többsége reflexből elutasítja. Helyette egy diszkrét sávon ajánljuk fel.
//
//  2. FCM TOKEN: az eszköz azonosítója, amire a szerver küldhet. A token
//     változhat (böngészőfrissítés, cache-törlés), ezért minden indulásnál
//     frissítjük.
//
//  3. IKON JELVÉNY (Badge API): az olvasatlan üzenetek száma az alkalmazás
//     ikonján. Telepített PWA-nál működik; a sima böngészőfülön a
//     dokumentum címe jelzi.
// ============================================================================

import { log } from "./log.js";
import { ENV } from "./env.js";

// Környezetenként más (env/<név>.json → "vapidKey"). A Firebase Console-ból
// szerezhető be:
//   Project settings → Cloud Messaging → Web Push certificates → Key pair
// Enélkül a böngészős értesítés NEM működik (a mobil PWA sem); üres érték
// esetén a push egyszerűen ki van kapcsolva.
const VAPID_KEY = ENV.vapidKey || "";

const PERMISSION_ASKED_KEY = "lineage0_notif_asked";

let messagingInstance = null;
let currentToken = null;

// ---------------------------------------------------------------------------
// A token-regisztráció ritkítása (2026-10-10, push-költségcsökkentés)
//
// Korábban az alkalmazás MINDEN indulásánál meghívódott a registerPushToken (1 függvényhívás + 1 olvasás + 1 írás),
// mert a memóriában tárolt token az oldal minden betöltésekor elfelejtődött. Most az utolsó sikeres regisztrációt
// a böngésző tárolja (localStorage); újra regisztrálunk, ha a token vagy a bejelentkezett tag más, illetve
// REG_ERVENYESSEG_MS-nál régebbi (az updatedAt frissen tartása miatt: a szerver a 60 napja nem frissült tokent
// törli, lásd functions/src/notify.js cleanupStalePushTokens).
// ---------------------------------------------------------------------------
const REG_KULCS = "lineage0_push_reg";
export const REG_ERVENYESSEG_MS = 30 * 24 * 3600 * 1000;

/** Kell-e a tokent (újra) regisztrálni a szerveren? (tiszta döntés - tesztelhető) */
export function kellRegisztralni({ token, uid, mentett, most }) {
  if (!token || !uid) return true;
  if (!mentett || mentett.token !== token || mentett.uid !== uid || typeof mentett.at !== "number") return true;
  return most - mentett.at >= REG_ERVENYESSEG_MS || most < mentett.at;
}

function mentettRegisztracio() {
  try { return JSON.parse(localStorage.getItem(REG_KULCS)); } catch (e) { return null; }
}
function regMentese(token, uid) {
  try { localStorage.setItem(REG_KULCS, JSON.stringify({ token, uid, at: Date.now() })); } catch (e) { /* nem kritikus */ }
}
function regTorlese() {
  try { localStorage.removeItem(REG_KULCS); } catch (e) { /* nem kritikus */ }
}

// ---------------------------------------------------------------------------
// KI/BE KAPCSOLÓ a profilban (2026-10-16, tagi kérés)
//
// A böngészőengedélyt programból nem lehet visszavonni, ezért a "ki" állapotot MI jegyezzük meg: tagonként és eszközönként
// (localStorage) egy jelző. Ha a jelző áll, az indulás nem regisztrálja újra az eszközt, a szerverről pedig a token törlődik.
// Tagonként külön kulcs: egy közös eszközön az egyik tag "ki"-je nem némítja el a másikat.
// ---------------------------------------------------------------------------
const PUSH_KI_ELOTAG = "lineage0_push_off:";

/** Kikapcsolta-e a tag az értesítést ezen az eszközön? */
export function pushKikapcsolva(uid) {
  if (!uid) return false;
  try { return localStorage.getItem(PUSH_KI_ELOTAG + uid) === "1"; } catch (e) { return false; }
}

function pushKikapcsolasJelolese(uid, ki) {
  if (!uid) return;
  try {
    if (ki) localStorage.setItem(PUSH_KI_ELOTAG + uid, "1");
    else localStorage.removeItem(PUSH_KI_ELOTAG + uid);
  } catch (e) { /* nem kritikus */ }
}

/**
 * A profil kapcsolójának állapota (tiszta döntés a böngésző és a tárolt jelző alapján).
 * tamogatott: false -> a kapcsoló nem használható (nincs push-támogatás, vagy a kiadásban nincs VAPID-kulcs);
 * engedely: "granted" | "denied" | "default" | "unsupported"; be: kapott-e ezen az eszközön értesítést.
 */
export function pushAllapot(uid, env = ENV) {
  if (!isSupported() || !pushConfiguredFor(env)) return { tamogatott: false, engedely: "unsupported", be: false };
  const engedely = Notification.permission;
  return { tamogatott: true, engedely, be: engedely === "granted" && !pushKikapcsolva(uid) };
}

let unreadCount = 0;

/**
 * Be van-e egyáltalán állítva a push a kiadáshoz?
 *
 * VAPID-kulcs nélkül a böngésző nem tud eszköztokent adni, tehát a szerver
 * SOHA nem tud értesítést küldeni. Ilyenkor értelmetlen (és félrevezető)
 * engedélyt kérni a felhasználótól: megadná, aztán semmi nem történne.
 */
export function isPushConfigured() {
  return pushConfiguredFor(ENV);
}

/**
 * A fenti döntés tiszta, ENV-független alakja - így mindkét ága
 * ellenőrizhető teszttel (a modulba fordított ENV futásidőben nem
 * cserélhető ki).
 */
export function pushConfiguredFor(env) {
  return typeof (env && env.vapidKey) === "string" && env.vapidKey.length > 0;
}

/** Támogatja-e a böngésző az értesítéseket? */
export function isSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/**
 * Megmutassuk-e a felajánló sávot?
 * Csak akkor, ha a böngésző támogatja, még nem döntött a felhasználó, és
 * nem kérdeztük meg korábban. Egy elutasított kérést NEM ismétlünk - az
 * zaklatás lenne, és a böngésző úgyis véglegesen blokkolná.
 */
export function shouldOfferNotifications() {
  if (!isSupported()) return false;
  // Kulcs nélkül a push technikailag lehetetlen - ne kérjünk olyan
  // engedélyt, amivel utána semmit nem tudnánk kezdeni.
  if (!isPushConfigured()) return false;
  if (Notification.permission !== "default") return false;
  try {
    return window.localStorage.getItem(PERMISSION_ASKED_KEY) !== "1";
  } catch (e) {
    return true;
  }
}

export function markOfferDismissed() {
  try {
    window.localStorage.setItem(PERMISSION_ASKED_KEY, "1");
  } catch (e) {
    // nem kritikus
  }
}

/**
 * Engedélykérés és tokenregisztráció. FELHASZNÁLÓI GESZTUSBÓL kell hívni
 * (gombkattintás), különben a böngésző elutasítja.
 */
export async function enableNotifications(registerTokenFn, uid = null) {
  markOfferDismissed();

  if (!isSupported()) return { ok: false, reason: "unsupported" };

  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (err) {
    log.error("[push] engedélykérés hiba:", err);
    return { ok: false, reason: "error" };
  }

  if (permission !== "granted") {
    return { ok: false, reason: permission };
  }

  // Felhasználói gesztus: mindig regisztrál (a nyelv és a token ekkor frissül a szerveren).
  const token = await refreshToken(registerTokenFn, { uid, kenyszeritett: true });
  if (token) pushKikapcsolasJelolese(uid, false); // a profil kapcsolójával korábban kikapcsolt eszköz újra "be"
  return { ok: Boolean(token), reason: token ? "granted" : "no-token" };
}

/**
 * Kikapcsolás a profilból: az eszköz tokenje törlődik a szerverről (onnan nem megy rá több értesítés), és a "ki" jelző megmarad,
 * így a következő belépés sem regisztrálja újra. A jelzőt CSAK sikeres szerveroldali törlés után állítjuk be - hiba esetén a kapcsoló
 * "be" marad, és a tag újrapróbálhatja (különben "ki"-t mutatna, miközben az értesítések tovább jönnek).
 */
export async function disableNotifications(registerTokenFn, uid) {
  try {
    let token = currentToken;
    // Ha ebben a munkamenetben még nem kértünk tokent (pl. az indulás kihagyta), most lekérjük, hogy tudjuk, mit töröljünk.
    if (!token && isSupported() && Notification.permission === "granted" && VAPID_KEY) {
      const { getMessaging, getToken } = await import("firebase/messaging");
      const { getFirebaseApp } = await import("./firebaseClient.js");
      if (!messagingInstance) messagingInstance = getMessaging(getFirebaseApp());
      const registration = await navigator.serviceWorker.ready;
      token = await getToken(messagingInstance, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    }
    if (token && registerTokenFn) await registerTokenFn(token, true);
    currentToken = null;
    regTorlese();
    pushKikapcsolasJelolese(uid, true);
    markOfferDismissed(); // kikapcsolás után a felajánló sáv ne térjen vissza
    // A böngészőoldali feliratkozást is megszüntetjük (best effort): újabb bekapcsoláskor új tokent kap az eszköz.
    if (messagingInstance) {
      try {
        const { deleteToken } = await import("firebase/messaging");
        await deleteToken(messagingInstance);
      } catch (e) { /* nem kritikus: a szerveroldali token már törölve van */ }
    }
    return { ok: true };
  } catch (err) {
    log.error("[push] kikapcsolás sikertelen:", err);
    return { ok: false };
  }
}

/**
 * Az FCM token lekérése és elküldése a szervernek.
 * Minden indulásnál lefut, ha már van engedély - a token ugyanis
 * megváltozhat, és egy elavult tokenre küldött értesítés elvész.
 */
export async function refreshToken(registerTokenFn, { uid = null, kenyszeritett = false } = {}) {
  if (!isSupported() || Notification.permission !== "granted") return null;
  // A profilban kikapcsolt eszközt az indulás NEM regisztrálja újra (csak a kifejezett bekapcsolás: kenyszeritett).
  if (!kenyszeritett && pushKikapcsolva(uid)) return null;

  if (!VAPID_KEY) {
    log.error(
      "[push] Nincs beállítva a VAPID-kulcs (env/" + ENV.name + ".json → vapidKey). " +
      "Firebase Console → Project settings → Cloud Messaging → Web Push certificates"
    );
    return null;
  }

  try {
    // A Firebase Messaging modult csak IGÉNY SZERINT töltjük be: így a
    // kezdeti csomagméret nem nő azoknál, akik nem kérnek értesítést.
    const { getMessaging, getToken } = await import("firebase/messaging");
    const { getFirebaseApp } = await import("./firebaseClient.js");

    if (!messagingInstance) {
      messagingInstance = getMessaging(getFirebaseApp());
    }

    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) return null;

    const elozo = currentToken;
    currentToken = token; // a kijelentkezéskor (unregisterToken) szükséges, akkor is, ha most nem regisztrálunk
    // uid ismeretében a tárolt regisztrációból döntünk (ritkítva); uid nélkül a régi viselkedés: csak változáskor.
    const kell = kenyszeritett || (uid ? kellRegisztralni({ token, uid, mentett: mentettRegisztracio(), most: Date.now() }) : token !== elozo);
    if (kell && registerTokenFn) {
      await registerTokenFn(token);
      if (uid) regMentese(token, uid);
      log.debug("[push] token regisztrálva");
    }
    return token;
  } catch (err) {
    log.error("[push] token lekérése sikertelen:", err);
    return null;
  }
}

/** Kijelentkezéskor: a token eltávolítása, hogy ne kapjon értesítést más. */
export async function unregisterToken(registerTokenFn) {
  if (!currentToken || !registerTokenFn) return;
  try {
    await registerTokenFn(currentToken, true);
    currentToken = null;
    regTorlese(); // a következő belépő (vagy ugyanaz) újra regisztrál
  } catch (err) {
    log.error("[push] token eltávolítása sikertelen:", err);
  }
}

// ---------------------------------------------------------------------------
// Ikon jelvény (olvasatlan üzenetek száma)
// ---------------------------------------------------------------------------

/**
 * Az olvasatlan szám beállítása. Két helyen jelenik meg:
 *   - Az alkalmazásikonon (Badge API) - telepített PWA-nál, mobilon
 *   - A böngészőfül címében - mindenhol máshol
 */
export function setUnreadCount(count) {
  unreadCount = Math.max(0, count | 0);

  // --- Alkalmazásikon jelvénye (2026-10-13): a lap NEM állít számot az ikonra - az ikon jelzését a service worker teszi
  // ki az első változáskor (szám nélküli jelvény), és a megnyitás törli (pushJelzo.js). Ha nincs olvasatlan, a jelvény törlődik.
  try {
    if (unreadCount === 0 && "clearAppBadge" in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }
  } catch (e) {
    // A Badge API nem mindenhol támogatott - ez nem hiba.
  }

  // --- Böngészőfül címe
  document.title = unreadCount > 0 ? `(${unreadCount}) ${alapCim()}` : alapCim();
}

/**
 * A lap címe a darabszám-előtag NÉLKÜL.
 *
 * FONTOS, VALÓS HIBÁBÓL TANULVA: korábban az "eredeti" címet egyetlen
 * változó őrizte, az első híváskor rögzítve. A nyelvváltás viszont átírja a
 * lap címét (lásd view.js: applyDocumentLanguage), így a rögzített érték
 * elavult lett, és a régi nyelvű cím ragadt vissza minden olvasatlan-jelzés
 * frissítésekor. Ezért a címet MINDIG frissen olvassuk, és csak a
 * "(3) " alakú előtagot vágjuk le róla.
 */
function alapCim() {
  return String(document.title || "").replace(/^\(\d+\)\s+/, "");
}

export function incrementUnread() {
  setUnreadCount(unreadCount + 1);
}

/**
 * A fül-cím és az ikonjelvény számlálója SOSEM lehet nagyobb, mint a ténylegesen olvasatlan szobák száma (v219): a push-érkezéskor
 * növelt számláló eddig csak ablak-fókusz váltáskor nullázódott, ezért egy már elolvasott szoba után is megmaradhatott "(1)" / jelvény.
 * Ha nincs olvasatlan szoba, a jelvény is törlődik. Visszaadja, változott-e a szám.
 */
export function kapUnreadCount(olvasatlanSzobakSzama) {
  const max = Math.max(0, olvasatlanSzobakSzama | 0);
  if (unreadCount > max) {
    setUnreadCount(max);
    return true;
  }
  if (max === 0) {
    try {
      if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    } catch (e) {
      // A Badge API nem mindenhol támogatott - ez nem hiba.
    }
  }
  return false;
}

export function clearUnread() {
  setUnreadCount(0);
}

/**
 * Az előtérben érkező üzenetek kezelése.
 *
 * FONTOS: ha az ablak LÁTHATÓ és a felhasználó épp abban a szobában van,
 * NEM mutatunk értesítést - az zavaró lenne, hiszen épp olvassa. Ilyenkor
 * a jelvényt sem növeljük.
 */
export function handleIncomingMessage({ roomId, currentRoomId }) {
  const ablakLathato = !document.hidden;
  const ugyanazASzoba = roomId === currentRoomId;

  if (ablakLathato && ugyanazASzoba) {
    clearUnread();
    return;
  }

  incrementUnread();
}
